import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import * as suryaDesign from "@tSurya/theme/design.surya";
import { getCurrentDesign } from "@tSurya/theme/design";
import buildSuryaThemeFragment from "@tSurya/theme/buildSuryaThemeFragment";
import { myAccountThemeWrapper } from "@tSurya/Utils/MyAccountThemeWrapper";
import merge from "lodash/merge";
import React, { useEffect } from "react";
import { ThemeProvider, css } from "styled-components";

interface SuryaPageThemeWrapperProps {
    children: React.ReactNode;
}

/**
 * Forces Surya styling (colors, typography, fonts) onto a page subtree when the
 * active brand is MGBW or Global Views. For the Surya brand this is a no-op
 * passthrough, so Surya styling is never modified.
 *
 * Coverage:
 *  - Colors + typography: overridden via a nested styled-components ThemeProvider
 *    that merges the Surya theme fragment on top of the active brand theme. This
 *    cascades to Mobius components (Button, Modal, Typography, etc.), getColor()
 *    based styles, and props.theme based styles, including portaled modals which
 *    keep React context.
 *  - Fonts: the Surya font stylesheet is injected and a global font-family
 *    override is applied to the page subtree as a belt-and-suspenders fallback.
 */
const SuryaPageThemeWrapper: React.FC<SuryaPageThemeWrapperProps> = ({ children }) => {
    const isSuryaBrand = getCurrentDesign() === suryaDesign;

    useEffect(() => {
        if (isSuryaBrand || typeof document === "undefined") {
            return;
        }

        const fontUrl = suryaDesign.TYPOGRAPHY.fontFamilyImportUrl;
        if (!fontUrl || document.querySelector(`link[href="${fontUrl}"]`)) {
            return;
        }

        const link = document.createElement("link");
        link.href = fontUrl;
        link.rel = "stylesheet";
        link.type = "text/css";
        link.setAttribute("data-theme", "surya");
        document.head.appendChild(link);
    }, [isSuryaBrand]);

    if (isSuryaBrand) {
        return <>{children}</>;
    }

    const applySuryaTheme = (outerTheme: BaseTheme): BaseTheme => {
        // Theme merging traverses brand objects that may be backed by lazy proxies
        // (see theme/design.ts). lodash.merge can throw while probing those proxies.
        // If anything goes wrong we must NOT let the ThemeProvider render function
        // throw, otherwise React unmounts the whole wrapped widget (the page header,
        // buckets, etc.) via the error boundary and all the static Surya styling is
        // lost. Falling back to the untouched outer theme keeps the subtree rendering
        // with its own hardcoded Surya `!important` styles.
        try {
            const fragment = buildSuryaThemeFragment();
            // Only the colors, typography and button subtrees are re-merged so the rest
            // of the theme (functions, React components, etc.) keeps its original
            // references. Buttons are deep-merged so only the Surya color is re-pinned
            // while the brand's baked css/geometry is preserved.
            return {
                ...outerTheme,
                colors: merge({}, outerTheme.colors, fragment.colors),
                typography: merge({}, outerTheme.typography, fragment.typography),
                button: merge({}, outerTheme.button, fragment.button),
            } as BaseTheme;
        } catch {
            return outerTheme;
        }
    };

    // Page-scoped baseline so inherited text/labels (e.g. "Updated by", "Private",
    // "Project Total") use Surya's dark text color instead of the global body color
    // baked from the active brand. Only a plain `color` is set on the subtree root,
    // so elements that set their own color (links, accent text) are unaffected. This
    // wrapper is only rendered for MGBW / Global Views (Surya short-circuits above),
    // so Surya styling is never touched.
    const wrapperCss = css`
        ${myAccountThemeWrapper.css}
        color: ${suryaDesign.COLORS.darkmagenta};
    `;

    return (
        <ThemeProvider theme={applySuryaTheme}>
            <StyledWrapper css={wrapperCss}>{children}</StyledWrapper>
        </ThemeProvider>
    );
};

export default SuryaPageThemeWrapper;
